import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddAIVoiceoverSceneGroupTool } from 'js/src/agent/tools/AddAIVoiceoverSceneGroupTool';
import { AddSceneTool } from 'js/src/agent/tools/AddSceneTool';
import { AddTalkingHeadSceneGroupTool } from 'js/src/agent/tools/AddTalkingHeadSceneGroupTool';
import { AddVoiceoverSceneTool } from 'js/src/agent/tools/AddVoiceoverSceneTool';
import { CreateBlankVideoTool } from 'js/src/agent/tools/CreateBlankVideoTool';
import { GetVideoStateTool } from 'js/src/agent/tools/GetVideoStateTool';
import { ListSceneDesignTool } from 'js/src/agent/tools/ListSceneDesignTool';
import { RebuildVideoTool } from 'js/src/agent/tools/RebuildVideoTool';
import { SetMediaTool } from 'js/src/agent/tools/SetMediaTool';
import { SetTextTool } from 'js/src/agent/tools/SetTextTool';
import { SplitSceneGroupTool } from 'js/src/agent/tools/SplitSceneGroupTool';

/**
 * `instructions` is a getter, not a plain string, on purpose. The tool classes it
 * interpolates transitively import the skills registry (CreateBlankVideoTool ->
 * loadedSkills -> skillStore -> definitions), so this module and those tools form
 * an import cycle. A top-level template literal would read `Tool.toolName` while a
 * tool module is still mid-evaluation (production enters via the tools), reading an
 * uninitialized class. Deferring the interpolation to first access — which only
 * happens at runtime when LoadSkillTool serves the skill — sidesteps the cycle
 * entirely, since every module is fully initialized by then.
 */
export const changeTemplateSkill: Skill = {
    name: SKILL_NAMES.changeTemplate,
    get description(): string {
        return `How to switch a video's template (or start it over) with \`${RebuildVideoTool.toolName}\`. Load BEFORE changing a built video's template or restarting it.`;
    },
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Changing a Video's Template (or Starting the Video Over)

How to switch a built video to a different template — or restart it from scratch —
with \`${RebuildVideoTool.toolName}\`. **This is destructive:** it deletes every
scene and design in the video, resetting it to a single blank starting point on the
chosen template. The video itself is kept (same id, same tab); from that blank
starting point you rebuild all the content under the new template. The one thing
preserved is voice-driven narration (its original audio + transcript) — see below.

## When to use
- The user wants a different template or a different look ("use this template
  instead", "make it more fun", "try another style"), or wants to start the video
  over. Use \`${RebuildVideoTool.toolName}\`.
- The user wants to add a voiceover / narration to a video that has none yet, or
  otherwise change the video's fundamental format ("add a voiceover", "make this a
  talking head video"). This requires recreating the video, so reset it with
  \`${RebuildVideoTool.toolName}\` first, then build the new structure — e.g. the
  voiceover scene groups — from the content you captured.
- Never empty a video by deleting every scene, and never call
  \`${CreateBlankVideoTool.toolName}\` when a video already exists — that only
  starts a brand-new video when none exists.

## 1. Capture the current video first
Before rebuilding, call \`${GetVideoStateTool.toolName}\` so you hold the full
pre-rebuild content — scripts, on-screen text, media ids, and structure. The
rebuild resets the video, so this captured state is how you recreate it under the
new template.

## 2. Do the swap
Call \`${RebuildVideoTool.toolName}\`:
- Pass the new \`template_id\` to switch templates (and \`aspect_ratio\` only if
  you are changing it).
- Omit \`template_id\` to restart on the same template.
- Music and aspect ratio carry over automatically — do not re-add them.
- Voice-driven narration (voiceover / talking head) is preserved automatically with
  its original audio + transcript.

The tool returns a short confirmation — including whether narration was preserved —
plus the new blank starting state. Rebuild against that starting point.

## 3. Rebuild under the new template
What to do depends on the scene group types in the pre-rebuild video (the video state
types each group as \`regular\`, \`ai_voiceover\`, \`recorded_voiceover\`,
\`ai_talking_head\`, or \`recorded_talking_head\`). Read them from the state you
captured in step 1 and the tool's confirmation. A video can have both kinds, so
handle each scene group by its type.

### Regular scene groups (text on media, no audio)
These are wiped by the rebuild. If the whole video was regular, it now has a single
blank scene on the new template. Recreate each scene:
- Keep the same script / on-screen text, and reuse the same media with
  \`${SetMediaTool.toolName}\` — the media ids are still valid, so there is no need
  to search again.
- Pick a design from the new template with \`${ListSceneDesignTool.toolName}\` and
  adapt the layout to it rather than forcing an exact match.
- The lone blank scene cannot be deleted while it is the only scene. Reuse it as
  your first scene, or add your other scenes with \`${AddSceneTool.toolName}\` first
  and then delete it — do not try to delete it before adding others.

### Voice-driven scene groups (voiceover / talking head)
The narration is already re-established with its original audio and transcript. Do
NOT regenerate it — do NOT call \`${AddAIVoiceoverSceneGroupTool.toolName}\` or
\`${AddTalkingHeadSceneGroupTool.toolName}\`.
- Assign designs from the new template to the narration scenes, and add the
  on-screen text / overlays / media (\`${SetTextTool.toolName}\`,
  \`${SetMediaTool.toolName}\`) to match.
- Adjust the scene splits only if the narration structure genuinely needs it
  (\`${AddVoiceoverSceneTool.toolName}\` / \`${SplitSceneGroupTool.toolName}\`).

### Videos with both
The voice-driven groups are preserved — rebuild their overlays and designs as in the
voice-driven section. The regular groups were cleared — recreate them as in the
regular section.`;
    },
};
